import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { PageWrapperComponent } from '../../../components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { HeavyHeaderComponent } from '../../../components/ui-kit/typography/heavy-header/heavy-header.component';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { BaseSelectComponent } from 'app/components/ui-kit/inputs/base-select/base-select.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { AircraftOption, aircraftOptionsNetJets } from 'app/constants/aircraft-constants';
import { UserService } from 'app/services/user.service';
import { AppStateService } from 'app/services/app-state.service';
import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { SectionLabelComponent } from '../../../components/ui-kit/typography/section-label/section-label.component';
import { ToggleRowComponent } from '../../../components/distinct-components/settings/toggle-row/toggle-row.component';
import { ProviderNames, normalizeProvider } from 'app/constants/provider-constants';
import { isIndependentAircraftOwner } from 'app/utils/membership-utils';
import { TextButtonComponent } from 'app/components/ui-kit/buttons/text-button/text-button.component';

interface Preference {
  key: string;
  prefLabel: string;
  usageLabel: string;
  preference: boolean;
  usage: boolean;
}

@Component({
  selector: 'app-membership',
  standalone: true,
  imports: [
    PageWrapperComponent,
    HeavyHeaderComponent,
    CommonModule,
    FormsModule,
    BaseInputComponent,
    BaseSelectComponent,
    PrimaryBtnComponent,
    AlertMessageComponent,
    SectionLabelComponent,
    ToggleRowComponent,
    AlertMessageComponent,
    TextButtonComponent
  ],
  templateUrl: './membership.component.html',
  styleUrls: ['./membership.component.scss']
})
export class MembershipComponent implements OnInit {
  // user + route
  userProfile: any;
  membershipIdToEdit: string | null = null;
  isEditing = false;
  showPrefs = true;
  isAircraftOnlyEdit = false;

  // form fields
  membershipProvider: string = ProviderNames.NETJETS;
  membershipProviders: string[] = [ProviderNames.NETJETS, ProviderNames.FLEXJET, ProviderNames.WHEELS_UP];

  membershipType = null;
  membershipTypes = ['Fractional Owner', 'Aircraft Lease', 'Jet Card', 'Other'];

  aircraftOptions: AircraftOption[] = aircraftOptionsNetJets;
  defaultAircraft: AircraftOption | null = null;
  seatingConfiguration = 0;
  isOtherAircraft = false;
  otherAircraft = '';
  newId: any;

  // only relevant when NOT editing
  preferences: Preference[] = [
    { key: 'children', prefLabel: 'I am ok with children (14 and under)', usageLabel: 'I generally bring children', preference: false, usage: false },
    { key: 'pets', prefLabel: 'I am ok with pets on board', usageLabel: 'I generally bring pets', preference: false, usage: false },
    { key: 'quiet', prefLabel: 'I prefer quiet flights', usageLabel: 'I am generally quiet on flights', preference: false, usage: false },
  ];

  isLoading = false;
  error: string | null = null;

  constructor(
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private appState: AppStateService,
  ) { }

  async ngOnInit() {
    this.userProfile = await this.userService.getUserProfile();

    this.membershipIdToEdit = this.route.snapshot.paramMap.get('id');
    if (this.membershipIdToEdit) {
      const backButton = {
        action: 'route',
        value: '/profile'
      }
      this.appState.navigation.backButtonState.set(backButton);
      this.isEditing = true;
      this.showPrefs = false;
      this.populateForEdit(this.membershipIdToEdit);
    }
  }

  private async populateForEdit(id: string) {
    this.userProfile = await this.userService.getUserProfile();
    const m = (this.userProfile.memberships || []).find((x: any) => x.id === id);
    if (!m) {
      this.error = 'Membership not found';
      return;
    }
    this.isAircraftOnlyEdit = isIndependentAircraftOwner(m);
    this.membershipProvider = normalizeProvider(m.provider);
    this.membershipType = m.type;
    if (m.isOtherAircraft) {
      this.isOtherAircraft = true;
      this.otherAircraft = m.defaultAircraft?.name || '';
      this.seatingConfiguration = m.seatingConfiguration;
    } else {
      this.isOtherAircraft = false;
      this.defaultAircraft = this.aircraftOptions.find(
        a => a.name === m.defaultAircraft.name
      ) || null;
      this.seatingConfiguration = m.seatingConfiguration;
    }
    // **do not touch this.preferences** when editing
  }

  onSelectAircraft(aircraft: AircraftOption | null) {
    if (aircraft && aircraft.name === 'Other') {
      this.isOtherAircraft = true;
      this.defaultAircraft = null;
      this.otherAircraft = '';
      this.seatingConfiguration = 0;
      return;
    }
    this.isOtherAircraft = false;
    this.otherAircraft = '';
    this.defaultAircraft = aircraft;
    if (aircraft) {
      this.seatingConfiguration = aircraft.defaultSeats;
    } else {
      this.seatingConfiguration = 0;
    }
  }

  viewList() {
    this.isOtherAircraft = false;
    this.otherAircraft = '';
  }

  onPrefToggle(evt: { setting: string; variant: string; value: boolean }) {
    const pref = this.preferences.find(p => p.key === evt.setting);
    if (pref) {
      (pref as any)[evt.variant] = evt.value;
    }
  }

  async submit(form: NgForm) {
    this.error = null;
    if (form.invalid) {
      this.error = 'Please fill all required required fields';
      return;
    }
    this.isLoading = true;

    // prepare updated memberships array
    const existing = Array.isArray(this.userProfile.memberships)
      ? [...this.userProfile.memberships]
      : [];
    if (this.isEditing && this.membershipIdToEdit) {
      const idx = existing.findIndex(m => m.id === this.membershipIdToEdit);
      const provider = normalizeProvider(this.membershipProvider);
      const defaultAircraft = this.isOtherAircraft
        ? { name: this.otherAircraft, category: 'Other', defaultSeats: this.seatingConfiguration }
        : this.defaultAircraft;
      existing[idx] = {
        ...existing[idx],
        provider,
        type: this.membershipType,
        defaultAircraft,
        seatingConfiguration: this.seatingConfiguration,
        isOtherAircraft: this.isOtherAircraft
        // no preferences touched here
      };
    } else {
      const id = (existing[0]?.id as string | undefined) ?? crypto.randomUUID();
      this.newId = id;

      const provider = normalizeProvider(this.membershipProvider);
      const defaultAircraft = this.isOtherAircraft
        ? { name: this.otherAircraft, category: 'Other', defaultSeats: this.seatingConfiguration }
        : this.defaultAircraft;

      // overwrite first slot
      existing[0] = {
        id,
        provider,
        type: this.membershipType,
        defaultAircraft,
        seatingConfiguration: this.seatingConfiguration,
        isOtherAircraft: this.isOtherAircraft,
      };

      // ensure we only keep one membership for launch
      if (existing.length > 1) existing.splice(1);
    }

    // on non-edit, also collect prefs and save to root profile
    let updates: any = { memberships: existing };
    if (!this.isEditing) {
      const prefsPayload = this.preferences.reduce((acc, p) => ({
        ...acc,
        [p.key]: { preference: p.preference, usage: p.usage }
      }), {});
      updates.preferences = prefsPayload;
    }

    try {
      await this.userService.updateUserProfile(updates);
      // const nextId = this.isEditing
      //   ? this.membershipIdToEdit
      //   : existing.at(-1)!.id;
      if (this.isEditing) {
        this.router.navigate(['/profile']);
        this.appState.navigation.backButtonState.set(null);
      } else {
        this.router.navigate(['/onboard/membership/verify', this.newId]);
      }
    } catch (err) {
      console.error(err);
      this.error = 'Failed to save membership';
    } finally {
      this.isLoading = false;
    }
  }

  onClickBack() {
    this.router.navigate(['/onboard/account-type']);
  }

}

